import { TargetFunction, TargetType } from '../store/reducers/workspace.reducer.ts';
import type { MeasurementData } from '../store/reducers/workspace.reducer.ts';
import type { RankedCandidate } from '../types/project.ts';

interface AggregateLayerResult {
  name: string;
  uuid: string;
  normId: number;
  expectedValues: ExpectedLike[];
  actualValues: ActualLike[];
}

export interface ExpectedActualPair {
  function: TargetFunction;
  type: TargetType;
  expected: number;
  actual: number;
}

type ExpectedLike = {
  function: TargetFunction;
  type: TargetType;
  value: number;
};

type ActualLike = {
  function: TargetFunction;
  type: TargetType;
  value: number;
};

/**
 * Builds expected/actual pairs with stable matching.
 * - Match strictly by function+type
 * - Skip unmatched expected values
 */
export const pairExpectedActualValues = (
  expectedValues: ExpectedLike[],
  actualValues: ActualLike[],
): ExpectedActualPair[] => {
  const pairs: ExpectedActualPair[] = [];
  const usedActualIndexes = new Set<number>();

  expectedValues.forEach((expected) => {
    const matchedIndex = actualValues.findIndex(
      (actual, index) =>
        !usedActualIndexes.has(index) && actual.function === expected.function && actual.type === expected.type,
    );

    if (matchedIndex === -1) return;

    const actual = actualValues[matchedIndex];
    usedActualIndexes.add(matchedIndex);

    pairs.push({
      function: expected.function,
      type: expected.type,
      expected: expected.value,
      actual: actual.value,
    });
  });

  return pairs;
};

export const getActualValueColor = (pair: ExpectedActualPair) => {
  if (pair.actual === null || pair.expected === null) return 'text-white';

  if (pair.type === 'AVERAGE') {
    // Red if below target, green if within +50%, yellow if above +50%
    if (pair.actual < pair.expected) {
      return 'text-red-400';
    } else if (pair.actual <= pair.expected * 1.5) {
      return 'text-green-400';
    } else {
      return 'text-yellow-500';
    }
  } else if (pair.type === 'EXACT') {
    // Green if within ±10% of expected, red otherwise
    const tolerance = Math.abs(pair.expected * 0.1);
    const difference = Math.abs(pair.actual - pair.expected);
    return difference <= tolerance ? 'text-green-400' : 'text-red-400';
  } else if (pair.type === 'MIN') {
    // Green if actual is above expected (meeting minimum), red if below
    return pair.actual >= pair.expected ? 'text-green-400' : 'text-red-400';
  } else if (pair.type === 'MAX') {
    // Green if actual is below expected (meeting maximum), red if above
    return pair.actual <= pair.expected ? 'text-green-400' : 'text-red-400';
  }

  return 'text-white';
};

export const isTargetSatisfied = (pair: ExpectedActualPair): boolean => {
  if (pair.type === 'AVERAGE') {
    return pair.actual >= pair.expected;
  } else if (pair.type === 'EXACT') {
    const tolerance = Math.abs(pair.expected * 0.1);
    const difference = Math.abs(pair.actual - pair.expected);
    return difference <= tolerance;
  } else if (pair.type === 'MIN') {
    return pair.actual >= pair.expected;
  } else if (pair.type === 'MAX') {
    return pair.actual <= pair.expected;
  }
  return false;
};

export const getComparisonOperator = (type: TargetType): string => {
  switch (type) {
    case 'MIN':
    case 'AVERAGE':
      return '≥';
    case 'MAX':
      return '≤';
    case 'EXACT':
      return '=';
    default:
      return '';
  }
};

/**
 * Computes satisfied/total targets from layer aggregate results.
 * Pairs expected/actual values using pairExpectedActualValues
 * (strict function+type matching, skipping unmatched expected values).
 * Returns { satisfied: 0, total: 0 } when no aggregate data is available.
 */
export function getTargetsSatisfiedCountFromAggregateResults(
  results: readonly AggregateLayerResult[] | null | undefined,
): { satisfied: number; total: number } {
  if (!results?.length) return { satisfied: 0, total: 0 };

  let total = 0;
  let satisfied = 0;
  for (const layer of results) {
    const pairs = pairExpectedActualValues(layer.expectedValues || [], layer.actualValues || []);
    for (const pair of pairs) {
      total++;
      if (isTargetSatisfied(pair)) satisfied++;
    }
  }
  return { satisfied, total };
}

/** Map live measurement layers into the aggregate shape used by market-match UI. */
export function measurementDataToAggregateResults(
  measurementData: MeasurementData[] | null | undefined,
): AggregateLayerResult[] {
  if (!measurementData?.length) return [];
  return measurementData
    .filter((layer) => (layer.expectedValues?.length ?? 0) > 0 && (layer.actualValues?.length ?? 0) > 0)
    .map((layer) => ({
      name: layer.name,
      uuid: layer.uuid,
      normId: layer.normId,
      expectedValues: layer.expectedValues,
      actualValues: layer.actualValues,
    }));
}

export function getTargetsSatisfiedCount(candidate: RankedCandidate): { satisfied: number; total: number } {
  return getTargetsSatisfiedCountFromAggregateResults(candidate.aggregateResults);
}
